/************************************************************************************************
 * Copyright TRUSST AI PTY LTD. All Rights Reserved.                                            *
 *                                                                                              *
 * Licensed under the TRUSST SOFTWARE LICENSE (the "License"). You may not use this file except *
 * in compliance with the "LICENSE" file accompanying this file. This file is distributed       *
 * on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, express or implied.       *
 *                                                                                              *
 * See the "License" file for the specific language governing permissions and limitations       *
 * under the License and limitations under the License.                                         *
 ***********************************************************************************************/

import {useAuth0} from '@auth0/auth0-react';
import {jwtDecode} from 'jwt-decode';
import {useEffect} from 'react';
import {useNavigate} from 'react-router-dom';
import {ROUTES} from '../routes';
import {useUserStore} from '../stores/userStore';

type DecodedAccessToken = {
  permissions?: string[];
};

export const usePopulateUserStore = () => {
  const {getAccessTokenSilently, user, isAuthenticated} = useAuth0();
  const setUser = useUserStore((state) => state.setUser);
  const resetUser = useUserStore((state) => state.resetUser);
  const navigate = useNavigate();

  useEffect(() => {
    const populateStore = async () => {
      if (!isAuthenticated || !user) {
        resetUser();
        return;
      }
      try {
        const token = await getAccessTokenSilently();
        const decoded: DecodedAccessToken = jwtDecode(token);
        const permissionsList = decoded.permissions || [];
        const permissionsMap = permissionsList.reduce((map, permission) => ({...map, [permission]: true}), {});
        setUser({
          userId: user.sub,
          username: user.username,
          firstName: user.given_name || user.user_metadata?.firstName,
          lastName: user.family_name || user.user_metadata?.lastName,
          permissionsList,
          permissionsMap,
        });
      } catch (error) {
        console.error('Error fetching or decoding token:', error);
        navigate(ROUTES.LOGOUT);
      }
    };
    void populateStore();
  }, [isAuthenticated, user]);
};
